import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Textarea, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ArrowIcon from "@/assets/ArrowIcon";
import AddOthersIcon from "@/assets/AddOthersIcon";
import RemoveOthersIcon from "@/assets/RemoveOthersIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import SelectCountry from "@/components/Selects/SelectCountry";
import SelectShip from "@/components/Selects/SelectShip";
import SelectCurrency from "@/components/Selects/SelectCurrency";
import SelectTypeShipOperator from "@/components/Selects/SelectTypeShipOperator";
import Loader from "@/components/Layouts/Loader";
import currency from "@/data/currency";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { getDataVessel } from "@/services/vesselService";
import { getInvoiceInspectorById, InvoiceInspectingCo, putInvoiceInspector } from "@/services/invoicesInspectorService";
import { invoiceInspectorSchema } from "@/schemas/invoiceInspectorSchema";
import { parseCurrencyFloat } from "@/utils/currency";
import { array10of0s, array1to10, otherArray, otherEvidenceFileIdArray, otherCostArray, setByIndex } from "@/utils/arrayState";
import EditFile from "@/components/Fields/EditFile";
import { Option } from "@/utils/select";

const EditInvoicesInspector = () => {
    const location = useLocation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const inspectorId = useAppSelector((state: RootState) => state.account.appId);
    const inspectorName = useAppSelector((state: RootState) => state?.account?.companyName);
    const isReviewer = useAppSelector((state: RootState) => state.account.isReviewer);

    const { id } = location.state || {};

    const [awaitingDays, setAwaitingDays] = useState(0);
    const [awaitingCost, setAwaitingCost] = useState(0);
    const [costInspection, setCostInspection] = useState(0);
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState<InvoiceInspectingCo | null>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [savedOthersFileIds, setSavedOthersFileIds] = useState<string[]>([]);
    const [defaultCurrency, setDefaultCurrency] = useState<any>(null);
    const [defaultShipName, setDefaultShipName] = useState<any>(null);
    const [isDraft, setIsDraft] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [listShipName, setListShipName] = useState<any[]>([]);
	const [prefix, setPrefix] = useState('IDR');
    const [total, setTotal] = useState(0);
    const [totalArray, setTotalArray] = useState<number[]>(array10of0s());
    const [uploadedFiles, setUploadedFiles] = useState<File[]>();
    const [visibleOthers, setVisibleOthers] = useState([1]);
    const [title, setTitle] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Update Invoice?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        if (isDraft) {
            setTitle("Save Invoice?");
            setConfirmationTest("Are you sure you want to save invoice as draft?");
            setConfirmationTitleButton("Save Invoice");
        } else {
            setTitle("Update Invoice?");
            setConfirmationTest("Are you sure you want to update?");
            setConfirmationTitleButton("Update Invoice");
        }

        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const vesselData = listShipName.find((x: Option) => x.value === dataEdit.shipName);

        const others: {desc: string, cost: string, file: File, fileId: string}[] = [];
        array1to10.map((num) => {
            const shouldExist = Boolean(dataEdit[`other${num}`] || dataEdit[`otherCost${num}`]);
            if (shouldExist){
                others.push({
                    desc: dataEdit[`other${num}`] ?? '',
                    cost: dataEdit[`otherCost${num}`] ? parseCurrencyFloat(dataEdit[`otherCost${num}`]).toString() : '',
                    file: dataEdit[`otherCostFile${num}`] ?? '',
                    fileId: dataDetail?.[otherEvidenceFileIdArray[num-1]] ?? '',
                })
            }
        });

        const data = new FormData();
        data.append('invoiceId', id);
        data.append('inspectorId', inspectorId);
        data.append('sequenceID', dataDetail?.sequenceID || '');
        data.append('inspectorName', inspectorName);
        data.append('vesselId', vesselData?.value || '');
        data.append('invoiceNumber', dataEdit.invoiceNumber);
        data.append('sireType', dataEdit?.sireType || '');
        data.append('shipName', vesselData?.label || '');
        data.append('invoiceDate', dataEdit.invoiceDate ? format(dataEdit.invoiceDate, FORMAT_ADD_DATE) : '');
        data.append('inspectionDate', dataEdit.inspectionDate ? format(dataEdit.inspectionDate, FORMAT_ADD_DATE) : '');
        data.append('country', dataEdit.country);
        data.append('port', dataEdit.port);
        data.append('jobDescription', dataEdit.jobDescription);
        data.append('costInspection', dataEdit.costInspection ? parseCurrencyFloat(dataEdit.costInspection).toString() : '');
        data.append('awaitingDays', dataEdit.awaitingDays ? parseCurrencyFloat(dataEdit.awaitingDays).toString() : '');
        data.append('awaitingDaysCost', dataEdit.awaitingCost ? parseCurrencyFloat(dataEdit.awaitingCost).toString() : '');
        array1to10.map((num, index) => {
            const obj = others[index];
            if (obj){
                data.append(`Other${num}`, obj.desc ?? '');
                data.append(`OtherCost${num}`, obj.cost ?? '');
                data.append(`OtherEvidence${num}File`, obj.file ?? '');
                data.append(`OtherEvidence${num}Id`, obj.fileId ?? '');
            } else {
                data.append(`Other${num}`, '');
                data.append(`OtherCost${num}`, '');
                data.append(`OtherEvidence${num}File`, '');
                data.append(`OtherEvidence${num}Id`, '');
            }
        });
        data.append('subTotalCost', total.toString());
        data.append('totalAmount', total.toString());
        data.append('currency', currency.find((x: Option) => x.value === dataEdit.currency)?.label ?? '');
        if (typeof(uploadedFiles) === 'object' && uploadedFiles?.length > 0) {
            for (let i = 0; i < uploadedFiles?.length; i++) {
                data.append('files', uploadedFiles[i]);
            }
        } else {
            data.append('files', '');
        }
        data.append('InspectionResultfile', dataEdit.inspectionResultFile ?? '');
        data.append('invoiceStatus', isDraft ? '0' : '2');
        data.append('paymentStatus', '0');
        data.append('note', '');
        if (dataDetail?.inspectionResultId) {
            data.append('inspectionResultId', dataDetail?.inspectionResultId || '');
        }
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Update Invoice?") {
            //navigate(role === 'super admin' ? '/manage-invoices/inspecting-company' : '/manage-invoices-inspecting-company');
            history.back();
            return;
        }

        const obj = generateDataEdit();
        const response = await putInvoiceInspector(obj) as any;
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Invoice updated.',
                description: "We've updated invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                //navigate(role === 'super admin' ? '/manage-invoices/inspecting-company' : '/manage-invoices-inspecting-company');
                history.back();
            }, 1000)
        } else {
            toast({
                title: 'Invoice failed to update.',
                description: "We unabled to update invoice for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleAddOthers = (num: number, isSubmitting: boolean) => {
        if (isSubmitting) {
            return;
        }

        const isExist = visibleOthers.includes(num + 1);
        if (!isExist) {
            setVisibleOthers([...visibleOthers, num + 1]);
        }
    }

    const handleRemoveOthers = (num: number, isSubmitting: boolean, setFieldValue: (fieldName: string, value: any) => void) => {
        if (isSubmitting) {
            return;
        }

        const isExist = visibleOthers.includes(num);
        if (isExist) {
            const newArr = visibleOthers.filter((x: number) => x !== num);
            setVisibleOthers(newArr);
            setTotalArray(setByIndex(totalArray, 0, num-1));
            setFieldValue(otherArray[num-1], "");
            setFieldValue(otherCostArray[num-1], "");
            setFieldValue(`otherCostFile${num}`, "");
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeCostInspection = (value: number, setFieldValue: any) => {
        setFieldValue(`costInspection`, value);
        setCostInspection(value);
    }

    const handleChangeAwaitingDays = (value: number, setFieldValue: any) => {
        setFieldValue(`awaitingDays`, value);
        setAwaitingDays(value);
    }

    const handleChangeAwaitingCost = (value: number, setFieldValue: any) => {
        setFieldValue(`awaitingCost`, value);
        setAwaitingCost(value);
    }

    const handleChangeOtherCost = (index: number, value: number, setFieldValue: any) => {
        //setTotal(0);

        setTotalArray(setByIndex(totalArray, value, index-1))
        setFieldValue(`otherCost${index}`, value);
    }

    const handleChangeCurrency = (props: any, value: string) => {
        props.setFieldValue('currency', value);
        setPrefix(value);
    }

    const handleDrop = (acceptedFiles: File[], fieldName: string, setFieldValue: any) => {
        setUploadedFiles(acceptedFiles);
        setFieldValue(fieldName, acceptedFiles);
    };

    const getDataDetail = async () => {
        const response = await getInvoiceInspectorById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            const myCurrency = currency?.find((x: any) => x.label === response.data?.currency);
            if (myCurrency) {
                setDefaultCurrency(myCurrency);
                setPrefix(myCurrency.value);
            }

            const totals: number[] = [];
            const res: number[] = [];
            const othersFile: string[] = [];
            array1to10.map((num) => {
                totals.push(response.data[`otherCost${num}`] ?? 0)
                if (response.data[`other${num}`] || response.data[`otherCost${num}`] > 0 || response.data[`otherEvidence${num}Id`]){
                    res.push(num);
                    othersFile.push(response.data[`otherEvidence${num}Id`]);
                }
            });

            if (res.length === 0){
                res.push(1);
            }
            setTotalArray(totals);
            setVisibleOthers(res);
            setSavedOthersFileIds(othersFile);

            setDataDetail(response.data);
            setCostInspection(response.data?.costInspection);
            setAwaitingDays(response.data?.awaitingDays);
            setAwaitingCost(response.data?.awaitingDaysCost);
            setTotal(response.data?.totalAmount);
            setDefaultShipName({ value: response.data?.vesselId, label: response.data?.shipName });

        } else {
            toast({
                title: 'Failed to load invoice data.',
                description: "We unabled to load invoice data for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const getListShipName = async () => {
        const response = await getDataVessel() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.vesselId, label: item.vesselName }));
            setListShipName(formattedOptions);
        }
    }

    useEffect(() => {
        getListShipName();
    }, []);

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    useEffect(() => {
        const awaitingFee = awaitingDays * awaitingCost;

        const total =
            (costInspection ? Number(costInspection) : 0) +
            (awaitingFee ? Number(awaitingFee) : 0) +
            totalArray.reduce((prev, curr) => prev + curr, 0);
        setTotal(total);
    }, [
        costInspection,
        awaitingDays,
        awaitingCost,
        totalArray
    ]);

    if (defaultShipName === null) {
        return (
            <Loader />
        );
    } else {
        return (
            <>
                <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                <div className="form-container">
                    <Formik
                        initialValues={{
                            sireType: dataDetail?.sireType,
                            invoiceNumber: dataDetail?.invoiceNumber,
                            shipName: defaultShipName.value,
                            inspectionDate: dataDetail?.inspectionDate,
                            invoiceDate: dataDetail?.invoiceDate,
                            country: dataDetail?.country,
                            port: dataDetail?.port,
                            jobDescription: dataDetail?.jobDescription || '',
                            costInspection: dataDetail?.costInspection === 0 ? '' : dataDetail?.costInspection,
                            awaitingDays: dataDetail?.awaitingDays,
                            awaitingCost: dataDetail?.awaitingDaysCost === 0 ? '' : dataDetail?.awaitingDaysCost,
                            other1: dataDetail?.other1 || '',
                            otherCost1: dataDetail?.otherCost1 === 0 ? '' : dataDetail?.otherCost1 ?? undefined,
                            otherCostFile1: '',
                            other2: dataDetail?.other2 || '',
                            otherCost2: dataDetail?.otherCost2 === 0 ? '' : dataDetail?.otherCost2 ?? undefined,
                            otherCostFile2: '',
                            other3: dataDetail?.other3 || '',
                            otherCost3: dataDetail?.otherCost3 === 0 ? '' : dataDetail?.otherCost3 ?? undefined,
                            otherCostFile3: '',
                            other4: dataDetail?.other4 || '',
                            otherCost4: dataDetail?.otherCost4 === 0 ? '' : dataDetail?.otherCost4 ?? undefined,
                            otherCostFile4: '',
                            other5: dataDetail?.other5 || '',
                            otherCost5: dataDetail?.otherCost5 === 0 ? '' : dataDetail?.otherCost5 ?? undefined,
                            otherCostFile5: '',
                            other6: dataDetail?.other6 || '',
                            otherCost6: dataDetail?.otherCost6 === 0 ? '' : dataDetail?.otherCost6 ?? undefined,
                            otherCostFile6: '',
                            other7: dataDetail?.other7 || '',
                            otherCost7: dataDetail?.otherCost7 === 0 ? '' : dataDetail?.otherCost7 ?? undefined,
                            otherCostFile7: '',
                            other8: dataDetail?.other8 || '',
                            otherCost8: dataDetail?.otherCost8 === 0 ? '' : dataDetail?.otherCost8 ?? undefined,
                            otherCostFile8: '',
                            other9: dataDetail?.other9 || '',
                            otherCost9: dataDetail?.otherCost9 === 0 ? '' : dataDetail?.otherCost9 ?? undefined,
                            otherCostFile9: '',
                            other10: dataDetail?.other10 || '',
                            otherCost10: dataDetail?.otherCost10 === 0 ? '' : dataDetail?.otherCost10 ?? undefined,
                            otherCostFile10: '',
                            totalAmount: dataDetail?.totalAmount,
                            currency: defaultCurrency?.value,
                            files: null,
                            inspectionResultFile: null,
                            invoiceStatus: dataDetail?.invoiceStatus,
                            paymentStatus: dataDetail?.paymentStatus,
                            note: dataDetail?.note || ''
                        }}
                        validationSchema={() => invoiceInspectorSchema(isDraft, savedOthersFileIds, true, dataDetail?.invoiceDownloadId, isReviewer)}
                        onSubmit={(values) => {
                            handleSubmit(values);
                        }}
                        enableReinitialize={true}
                    >
                        {(props) => (
                            <Form>
                                {isReviewer && <Field name='sireType'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.sireType && form.touched.sireType)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">SIRE Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <SelectTypeShipOperator
                                                        name='sireType'
                                                        placeholder="Choose Type"
                                                        handleChange={({ value }: { value: string }) => form.setFieldValue('sireType', value)}
                                                        value={form.values.sireType}
                                                        haserror={Boolean(form.errors.sireType && form.touched.sireType)}
                                                        defaultValue={props.values.sireType}
                                                    />
                                                    <FormErrorMessage>{String(form.errors.sireType)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>}
                                <Field name='invoiceNumber'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.invoiceNumber && form.touched.invoiceNumber)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Invoice Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Input {...field} placeholder='Invoice Number' className="form-input" isDisabled={isSubmitting} />
                                                    <FormErrorMessage>{String(form.errors.invoiceNumber)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='shipName'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.shipName && form.touched.shipName)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Vessel Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <SelectShip
                                                        name='shipName' placeholder="Choose Ship"
                                                        handleChange={({ value }: any) => props.setFieldValue('shipName', value)}
                                                        value={props.values.shipName} options={listShipName}
                                                        hasError={Boolean(form.errors.shipName && form.touched.shipName)}
                                                        isDisabled
                                                    />
                                                    <FormErrorMessage>{String(form.errors.shipName)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name="inspectionDate">
                                    {({ form, field }: any) => (
                                        <FormControl isInvalid={Boolean(form.errors.inspectionDate && form.touched.inspectionDate)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Inspection Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div style={{
                                                    width: '100%',
                                                    height: '2.5rem',
                                                    fontSize: '1rem',
                                                    paddingInlineStart: '1rem',
                                                    paddingInlineEnd: '1rem',
                                                    borderRadius: '0.375rem',
                                                    minWidth: '0px',
                                                    outline: '2px solid transparent',
                                                    outlineOffset: '2px',
                                                    position: 'relative',
                                                    appearance: 'none',
                                                    transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                    paddingTop: '0.35rem',
                                                    marginBottom: (form.errors.inspectionDate && form.touched.inspectionDate) ? '1.25rem' : 0,
                                                    border: form.errors.inspectionDate && form.touched.inspectionDate ? '2px solid red' : '1px solid #e5e7e9'
                                                }}>
                                                    <DatePicker
                                                        id="inspectionDate"
                                                        selected={field.value as Date | null}
                                                        onChange={(date: Date | null) => props.setFieldValue('inspectionDate', date)}
                                                        dateFormat="dd-MM-YYYY"
                                                        placeholderText="Inspection Date"
                                                        readOnly={isSubmitting}
                                                    />
                                                    <FormErrorMessage>{String(form.errors.inspectionDate)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='invoiceDate'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.invoiceDate && form.touched.invoiceDate)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Invoice Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div style={{
                                                    width: '100%',
                                                    height: '2.5rem',
                                                    fontSize: '1rem',
                                                    paddingInlineStart: '1rem',
                                                    paddingInlineEnd: '1rem',
                                                    borderRadius: '0.375rem',
                                                    minWidth: '0px',
                                                    outline: '2px solid transparent',
                                                    outlineOffset: '2px',
                                                    position: 'relative',
                                                    appearance: 'none',
                                                    transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                    paddingTop: '0.35rem',
                                                    marginBottom: (form.errors.invoiceDate && form.touched.invoiceDate) ? '1.25rem' : 0,
                                                    border: form.errors.invoiceDate && form.touched.invoiceDate ? '2px solid red' : '1px solid #e5e7e9'
                                                }}>
                                                    <DatePicker
                                                        id="invoiceDate"
                                                        selected={field.value as Date | null}
                                                        onChange={(date: Date | null) => props.setFieldValue('invoiceDate', date)}
                                                        dateFormat="dd-MM-YYYY"
                                                        placeholderText="Invoice Date"
                                                        readOnly={isSubmitting}
                                                    />
                                                    <FormErrorMessage>{String(form.errors.invoiceDate)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='country'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.country && form.touched.country)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} defaultValue={props.values.country} hasError={form.errors.country && form.touched.country} isDisabled={isSubmitting} />
                                                    <FormErrorMessage>{String(form.errors.country)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='port'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.port && form.touched.port)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Port<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Input {...field} placeholder='Port' className="form-input" isDisabled={isSubmitting} />
                                                    <FormErrorMessage>{String(form.errors.port)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='jobDescription'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.jobDescription && form.touched.jobDescription)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Job Description<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <Textarea {...field} placeholder='Job Description' className="form-input" disabled={isSubmitting} />
                                                    <FormErrorMessage>{String(form.errors.jobDescription)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='currency'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.currency && form.touched.currency)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel htmlFor='status-switch' className="form-label">
                                                    Currency<span style={{ color: 'red' }}>*</span>
                                                </FormLabel>
                                                <div className="form-input-container">
                                                    <SelectCurrency
                                                        name='currency'
                                                        placeholder="Choose Currency"
                                                        handleChange={({ value }: any) => handleChangeCurrency(props, value)}
                                                        value={props.values.currency}
                                                        defaultValue={defaultCurrency}
                                                        haserror={Boolean(form.errors.currency && form.touched.currency)}
                                                        isDisabled={isSubmitting}
                                                        width="100%"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.currency)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='costInspection'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.costInspection && form.touched.costInspection)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Cost Inspection<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Input Cost'
                                                        prefix={prefix}
                                                        value={props.values.costInspection}
                                                        error={Boolean(form.errors.costInspection && form.touched.costInspection)}
                                                        onValueChange={({ floatValue = 0 }) => handleChangeCostInspection(floatValue, props.setFieldValue)}
                                                        disabled={isSubmitting}
                                                        width="100%"
                                                    />
                                                    <FormErrorMessage>{String(form.errors.costInspection)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='awaitingDays'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.awaitingDays && form.touched.awaitingDays)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Awaiting Days</FormLabel>
                                                <div className="form-input-container">
                                                    <div>
                                                        <CurrencyInput
                                                            {...field}
                                                            placeholder='Input'
                                                            prefix=''
                                                            value={props.values.awaitingDays}
                                                            onValueChange={({ floatValue = 0 }) => handleChangeAwaitingDays(floatValue, props.setFieldValue)}
                                                            disabled={isSubmitting}
                                                            width='10em'
                                                            
                                                        />
                                                        <span style={{ marginLeft: '5px' }}>Days</span>
                                                    </div>
                                                    
                                                    <FormErrorMessage>{String(form.errors.awaitingDays)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='awaitingCost'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.awaitingCost && form.touched.awaitingCost)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label"></FormLabel>
                                                <div className="form-input-container">
                                                    <div>
                                                        <CurrencyInput
                                                            {...field}
                                                            placeholder='Input Cost'
                                                            prefix={prefix}
                                                            value={props.values.awaitingCost}
                                                            onValueChange={({ floatValue = 0 }) => handleChangeAwaitingCost(floatValue, props.setFieldValue)}
                                                            disabled={isSubmitting}
                                                            width='10em'
                                                        />
                                                        <span style={{ marginLeft: '5px' }}>/ Day</span>
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.awaitingCost)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                {array1to10.map((num /* 1-10 */, index /* 0-9 */) => visibleOthers.includes(num) && <>
                                    <Field name={otherArray[index]}>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors[otherArray[index]] && form.touched[otherArray[index]])} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Others</FormLabel>
                                                    <div className="form-input-container">
                                                        <Textarea {...field} placeholder='Description' className="form-input" isDisabled={isSubmitting} />
                                                        <FormErrorMessage>{String(form.errors[otherArray[index]])}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name={otherCostArray[index]}>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors[otherCostArray[index]] && form.touched[otherCostArray[index]])} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label"></FormLabel>
                                                    <div className="form-input-container">
                                                        <CurrencyInput
                                                            {...field}
                                                            placeholder='Input Cost'
                                                            prefix={prefix}
                                                            value={props.values[otherCostArray[index]]}
                                                            error={Boolean(form.errors[otherCostArray[index]] && form.touched[otherCostArray[index]])}
                                                            onValueChange={({ floatValue = 0 }) => handleChangeOtherCost(num, floatValue, props.setFieldValue)}
                                                            disabled={isSubmitting}
                                                            width="100%"
                                                        />
                                                        <FormErrorMessage>{String(form.errors[otherCostArray[index]])}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name={`otherCostFile${num}`}>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors[`otherCostFile${num}`] && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label"></FormLabel>
                                                    <div className="form-input-container">
                                                        <EditFile
                                                            fileId={dataDetail?.[otherEvidenceFileIdArray[index]] ?? undefined}
                                                            handleDrop={(e) => props.setFieldValue(`otherCostFile${num}`, e[0])} //handleDrop(e, 'otherCostFile1', props.setFieldValue)}
                                                            isInvalid={Boolean(form.errors[`otherCostFile${num}`] && form.submitCount)}
                                                            type="supporting-files"
                                                            isDisabled={props.isSubmitting}
                                                            title="Other File"
                                                        />
                                                        <FormErrorMessage>{String(form.errors[`otherCostFile${num}`])}</FormErrorMessage>
                                                        {!visibleOthers.includes(num+1) && <div>
                                                            {(num < 10) && <AddOthersIcon style={{ marginTop: '7px', marginLeft: '7px', cursor: 'pointer', display: 'inline' }} onClick={() => handleAddOthers(num, isSubmitting)} />}
                                                            {(num > 1) && <RemoveOthersIcon style={{ marginTop: '7px', marginLeft: '7px', cursor: 'pointer', display: 'inline' }} onClick={() => handleRemoveOthers(num, isSubmitting, props.setFieldValue)} />}
                                                        </div>}
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                </>)}
                                <Field name='totalAmount'>
                                    {({ field, form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.totalAmount && form.touched.totalAmount)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Total Amount<span style={{ color: 'red' }}>*</span></FormLabel>
                                                <div className="form-input-container">
                                                    <CurrencyInput
                                                        {...field}
                                                        placeholder='Total Amount'
                                                        prefix={prefix}
                                                        value={total}
                                                        width="100%"
                                                        disabled
                                                    />
                                                    <FormErrorMessage>{String(form.errors.totalAmount)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                                <Field name='inspectionResultFile'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.inspectionResultFile && form.touched.inspectionResultFile)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Inspection Result</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        handleDrop={(e) => props.setFieldValue('inspectionResultFile', e[0])}
                                                        fileId={dataDetail?.inspectionResultId}
                                                        isInvalid={Boolean(form.errors.files && form.submitCount)}
                                                        type="inspection-result"
                                                        title="Inspection Result File"
                                                        isDisabled={props.isSubmitting}
                                                    />
                                                    <FormErrorMessage>{String(form.errors.inspectionResultFile)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>

                                <Field name='files'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.files && form.submitCount)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <FormLabel className="form-label">Invoice Files</FormLabel>
                                                <div className="form-input-container">
                                                    <EditFile
                                                        title="Invoice files"
                                                        handleDrop={(e) => handleDrop(e, 'files', props.setFieldValue)}
                                                        fileId={dataDetail?.invoiceDownloadId}
                                                        isInvalid={Boolean(form.errors.files && form.submitCount)}
                                                        type="supporting-files"
                                                        isDisabled={props.isSubmitting}
                                                        isDownloadFolder
                                                        folderType={1}
                                                    />
                                                    <FormErrorMessage>{String(form.errors.files)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
    
                                <Divider />
                                <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
                                    <Button
                                        mt={4}
                                        bgColor='white'
                                        borderColor='#D0D5DD'
                                        isLoading={isSubmitting}
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        mt={4}
                                        bgColor='white'
                                        borderColor='#D0D5DD'
                                        isLoading={isSubmitting}
                                        onClick={() => setIsDraft(true)}
                                        type='submit'
                                    >
                                        Save as Draft
                                    </Button>
                                    <Button
                                        mt={4}
                                        bgColor={COLORS.BUTTON.BACKGROUND}
                                        color={COLORS.BUTTON.FOREGROUND}
                                        isLoading={isSubmitting}
                                        onClick={() => setIsDraft(false)}
                                        type='submit'
                                        rightIcon={<ArrowIcon />}
                                    >
                                        Send Invoice
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </>
        )
    }
}

export default EditInvoicesInspector;
